/**
 * Outlet context for the Quality Triage shell: one shared poll of the door's
 * threads + case files (useQcpInvestigations, 30s) feeds every tab, so the
 * shell's badges and the pages always agree on the data.
 */
import { useOutletContext } from 'react-router-dom';
import type { QcpInvestigationsState } from '../../components/portal/useQcpInvestigations';
import type { QcpDataRequestsState } from '../../components/portal/useQcpDataRequests';
import type { DeliveryOutcome } from './deliveryView';
import type { ResponseNotice } from './respondedView';
import type { AnswerRow } from './answersView';
import type { MerlinFollowupsState } from './useMerlinFollowups';
import type { FoundAnswersState } from './useFoundAnswers';

export interface QualityTriageContext {
  invState: QcpInvestigationsState;
  /** The door user's display name — drives the mine/routed-away split. */
  userName: string | null;
  /** True for a read-only viewer of the desk (quality:viewer, kanban
   *  808dabf6): the pages hide every write control. Optional: absent in
   *  older test harnesses (then writable). Components deeper than the
   *  pages read useQualityReadOnly() instead. */
  readOnly?: boolean;
  /** Bump after markThreadSeen so the shell recomputes its alert badges. */
  bumpSeen: () => void;
  /** The door's asks (data requests), one shared poll for the tabs that
   *  list them across cases. Optional: absent in older test harnesses. */
  dataRequests?: QcpDataRequestsState;
  /** Did each routed-away item's hand-off email arrive (by thread id), read
   *  once by the shell for the "More" alarm and reused by Routed away.
   *  Optional: absent in older test harnesses (the page then reads its own). */
  deliveryOutcomes?: Record<string, DeliveryOutcome | null>;
  /** Who has answered (respondedView.responseNotices), newest first: read
   *  once by the shell for the tab badges and reused by the lists and the
   *  Triage notice. Optional: absent in older test harnesses (none shown). */
  responses?: ResponseNotice[];
  /** Every answer not yet marked seen (answersView.answersForYou), newest
   *  first: Triage's "Answers for you" list and its Responded count.
   *  Optional: absent in older test harnesses (none shown). */
  answers?: AnswerRow[];
  /** Open one answer in the shell's drawer (sets ?answer=<request id>). */
  openAnswer?: (requestId: string) => void;
  /** The follow-ups Merlin promised the reader (kanban 43160d8b), one shared
   *  poll for the Pending tab's count, its rows and the item's "Merlin will
   *  check back" line. Optional: absent in older test harnesses (hidden). */
  followups?: MerlinFollowupsState;
  /** Answers the daily sweep found elsewhere (kanban 3eed0be5): the
   *  candidates waiting on the reader's confirm and what it closed this
   *  week. Optional: absent in older test harnesses (strip hidden). */
  foundAnswers?: FoundAnswersState;
}

export function useQualityTriage(): QualityTriageContext {
  return useOutletContext<QualityTriageContext>();
}
